import { useFormikContext } from 'formik'
import { ReactElement } from 'react'
import { defineMessages, FormattedMessage, useIntl } from 'react-intl'

import { Column } from 'components/v2/Column'
import Heading from 'components/v2/Heading'
import { Input } from 'components/Input'
import { Row } from 'components/v2/Row'
import { IPartnerSettingsFormValues } from 'features/Vehicles/types'
import { PartnerAutocomplete } from 'features/Partners/components/PartnerAutocomplete'
import { GTBusinessPartnerAutocomplete } from 'gql/types.generated'
import { Label } from 'components/v2/Label'
import { MultiDriverAutocomplete, IDriverAutocomplete } from 'features/Drivers/components/MultiDriverAutocomplete'

const messages = defineMessages({
  externalIdPlaceholder: {
    id: 'vehicles.form.partnerVehicleId.placeholder',
    defaultMessage: 'Enter the partner vehicle id'
  },
  partnerPlaceholder: {
    id: 'vehicles.form.partner.placeholder',
    defaultMessage: 'Select a partner'
  },
  driversPlaceholder: {
    id: 'vehicles.form.drivers.placeholder',
    defaultMessage: 'Select drivers'
  }
})

export const PartnerSettingsForm = ({ isEdit }: { isEdit?: boolean }): ReactElement => {
  const intl = useIntl()
  const { values, setFieldValue, setValues, handleBlur, touched, errors } =
    useFormikContext<IPartnerSettingsFormValues>()

  const handleExternalIDChange = ({ target: { value } }: React.ChangeEvent<HTMLInputElement>): void => {
    setFieldValue('externalID', value.toUpperCase())
  }

  return (
    <>
      <Heading.H4>
        <FormattedMessage id="vehicles.form.partnerSettings.title" defaultMessage="Partner settings" />
      </Heading.H4>
      <Row noMargin>
        <Column width="50%">
          <Input
            id="externalID"
            value={values.externalID || ''}
            onChange={handleExternalIDChange}
            onBlur={handleBlur}
            placeholder={intl.formatMessage(messages.externalIdPlaceholder)}
            label={<FormattedMessage id="vehicles.form.partnerVehicleId.label" defaultMessage="Partner vehicle id" />}
            touched={touched.externalID}
            errors={errors.externalID}
            optional
          />
        </Column>
        <Column width="50%">
          <PartnerAutocomplete
            label={<FormattedMessage id="vehicles.form.partner.label" defaultMessage="Partner" />}
            placeholder={intl.formatMessage(messages.partnerPlaceholder)}
            value={values.partner.name}
            onChange={(partner: GTBusinessPartnerAutocomplete): void => {
              setValues({
                ...values,
                partner: {
                  name: partner.name,
                  id: partner.id
                },
                drivers: []
              })
            }}
            touched={touched.partner?.id}
            errors={errors.partner?.id}
            disabled={isEdit}
            isActive
          />
        </Column>
      </Row>
      <Row noMargin>
        <Column width="50%">
          <Label optional>
            <FormattedMessage id="vehicles.form.drivers.label" defaultMessage="Assign drivers" />
          </Label>
          <MultiDriverAutocomplete
            onChange={(drivers: IDriverAutocomplete[]): void => {
              setFieldValue('drivers', drivers)
            }}
            placeholder={intl.formatMessage(messages.driversPlaceholder)}
            values={values.drivers}
            partnerId={values.partner?.id}
          />
        </Column>
      </Row>
    </>
  )
}
